import type { TemplateDictionaryItem } from "@/lib/data/static/templateDictionaryItems";
import { startCase } from "lodash";

export type TemplateDictionaryFormItem = TemplateDictionaryItem & {
  baseVariable: string;
  baseKey: string;
  isFormattingVariant: boolean;
  displayTitle: string;
  alias?: string;
  instance?: number;
};

type InstanceGroup = {
  key: string;
  alias?: string;
  instance: number;
  items: TemplateDictionaryFormItem[];
};

export const buildInstanceGroups = (
  items: TemplateDictionaryFormItem[],
): InstanceGroup[] => {
  const groups: InstanceGroup[] = [];
  items.forEach((item) => {
    const { alias, instance } = item;
    const resolvedInstance = instance ?? 1;
    const key = alias ? `alias:${alias}` : `instance:${resolvedInstance}`;
    const existingGroup = groups.find((group) => group.key === key);
    if (existingGroup) {
      existingGroup.items.push(item);
    } else {
      groups.push({ key, alias, instance: resolvedInstance, items: [item] });
    }
  });
  return groups;
};

export const getGroupHeadingSuffix = (
  group: InstanceGroup,
  totalGroups: number,
) => {
  if (group.alias) return ` (${startCase(group.alias)})`;
  return totalGroups > 1 || group.instance > 1 ? ` #${group.instance}` : "";
};

export const getVisibleGroupItems = (items: TemplateDictionaryFormItem[]) => {
  const representativeByBaseKey = new Map<string, TemplateDictionaryFormItem>();
  items.forEach((item) => {
    const existing = representativeByBaseKey.get(item.baseKey);
    if (
      !existing ||
      (existing.isFormattingVariant && !item.isFormattingVariant)
    ) {
      representativeByBaseKey.set(item.baseKey, item);
    }
  });
  return items.filter(
    (item) => representativeByBaseKey.get(item.baseKey) === item,
  );
};
